import { useEffect, useState } from 'react';
import { User, UserAddress } from '../../../../clients/quipli-service/user/user';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { Typography } from '../../../../components/typography/typography';
import { Dropdown } from '../../../../components/dropdown/dropdown';
import { Button } from '../../../../components/form/button';
import Icon from '../../../../components/icons/icon';
import { ConfirmModal } from '../../../../components/modal/confirm-modal';
import { UserAddressForm } from '../../components/user-address-form';
import { Card } from '../../../../components/card/card';
import { formatPhoneNumber } from '../../../../util/util';

export const Addresses = ({ user }: { user: User }) => {
  const { getUserAddresses } = useAPI();
  const [data, setData] = useState<UserAddress[]>([]);
  const [isModalAddOpen, setModalAddOpen] = useState<boolean>(false);

  const fetchAddresses = async () => {
    const addresses = await getUserAddresses(user.id);
    setData(addresses);
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  useEffect(() => {
    if (!isModalAddOpen) {
      fetchAddresses();
    }
  }, [isModalAddOpen]);

  return (
    <div className="tab-section">
      {data.length === 0 ? (
        <Card className="no_item_page">
          <Icon icon="Location02" size="large" />
          <Typography type="text-sm" weight="medium" className="property text-grey-500">
            Looks like you haven't added any client addresses yet.
            <br></br> Click “Add Address” to get started
          </Typography>
          <button
            className="save-button"
            onClick={() => {
              setModalAddOpen(true);
            }}
          >
            Add Address
          </button>
        </Card>
      ) : (
        <Card className="flex flex-column gap-12 p-8">
          {data.map((item, index) => (
            <AddressItem key={`address-item-${index}`} object={item} reload={fetchAddresses}></AddressItem>
          ))}
        </Card>
      )}
      <UserAddressForm
        isOpen={isModalAddOpen}
        onSubmit={() => setModalAddOpen(false)}
        onClose={() => setModalAddOpen(false)}
        userAddress={
          {
            id: 0,
            name: user.name,
            userId: user.id,
            phone: '',
            companyName: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            country: '',
            type: 'delivery',
            isGoogleGenerated: false,
            isDeleted: false,
          } as UserAddress
        }
      ></UserAddressForm>
    </div>
  );
};

const AddressItem = ({ object, reload }: { object: UserAddress; reload: () => void }) => {
  const [isModalDeleteOpen, setModalDeleteOpen] = useState<boolean>(false);
  const [isModalEditOpen, setModalEditOpen] = useState<boolean>(false);
  const { deleteUserAddress } = useAPI();

  const removeItem = async () => {
    if (!object) return;

    await deleteUserAddress(object.id);
    setModalDeleteOpen(false);
    reload();
  };

  const userAddressFormat = (address: UserAddress) => {
    let addressText = address.address1;
    if (address.isGoogleGenerated === false) {
      addressText += `${address.address2 ? `, ${address.address2}` : ''}${address.city ? `, ${address.city}` : ''}${
        address.state ? `, ${address.state}` : ''
      }${address.zip ? `, ${address.zip}` : ''}`;
    }

    return addressText;
  };

  useEffect(() => {
    if (!isModalEditOpen) {
      reload();
    }
  }, [isModalEditOpen]);

  return (
    <>
      <Card className="flex flex-column gap-4 w-fit">
        <div className="title">
          <div className="flex items-center gap-2">
            <Typography type="text-md" weight="semi-bold">
              {object.type == 'billing' ? 'Billing address' : 'Delivery address'}
            </Typography>
            <Icon icon={object.type == 'billing' ? 'CreditCardCheck' : 'Delivery'} size="medium" />
          </div>

          <div>
            <Dropdown
              trigger={() => (
                <Button className="button-tertiary">
                  <Icon icon="DotsHorizontal" stroke="var(--grey-800)" size="small" />
                </Button>
              )}
            >
              <div>
                <div
                  className="flex gap-2"
                  onClick={() => {
                    setModalEditOpen(true);
                  }}
                  data-cy="disable_customer"
                >
                  <Typography type="text-sm" className="text-quipli-black-800">
                    Edit Address
                  </Typography>
                </div>
              </div>
              <div>
                <div
                  className="flex gap-2"
                  onClick={() => {
                    setModalDeleteOpen(true);
                  }}
                  data-cy="delete_customer"
                >
                  <Typography type="text-sm" className="text-error-500">
                    Delete address
                  </Typography>
                </div>
              </div>
            </Dropdown>
          </div>
        </div>
        <div className="h-[1px] w-full bg-grey-300"></div>
        <Typography type="text-sm" weight="medium" className="text-quipli-black-800">
          {object.name}
        </Typography>
        <span className="quipli-v2 text-sm regular text-[var(--quipli-black-800)]">{userAddressFormat(object)}</span>
        <span className="quipli-v2 text-sm regular text-[var(--quipli-black-800)]">
          {formatPhoneNumber(object.phone)}
        </span>

        <ConfirmModal
          isOpen={isModalDeleteOpen}
          close={() => setModalDeleteOpen(false)}
          onConfirm={removeItem}
          headerIcon={
            <div className="flex justify-center">
              <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
            </div>
          }
          prompt="Remove item?"
          additionalText="This cannot be undone"
          noLabel="Cancel"
          yesLabel="Remove"
        />

        <UserAddressForm
          isOpen={isModalEditOpen}
          onSubmit={() => setModalEditOpen(false)}
          onClose={() => setModalEditOpen(false)}
          userAddress={object}
        ></UserAddressForm>
      </Card>
    </>
  );
};
